import * as React from "react";
import {Suspense, useEffect, useRef, useState} from "react";
import {BraintreeNamespace, BraintreePayPalButtons} from "@paypal/react-paypal-js";
import {isCart, IUnsafeCartContext, useUnsafeCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {CreateBillingAgreementActions} from "@paypal/react-paypal-js/dist/types/types/braintreePayPalButtonTypes";
import {BraintreePayPalCheckoutTokenizationOptions} from "@paypal/react-paypal-js/dist/types/types/braintree/paypalCheckout";
import {Cart, Checkout, ICheckoutPutParams, IGetCheckoutShippingOptionsData} from "../../../../typescript-library";
import {INTERNAL_ERROR_TYPE, Payments, retrieveCheckout} from "../PaymentController";
import {
  findShippingOption,
  getNewPaymentOptions,
  getPaymentOptions,
  setPayPalInfoOnViewState,
} from "./helpers/paypalHelperFunctions";
import {ErrorPlaceInfoType, onErrorClosure} from "./helpers/onErrorClosure";
import {
  CreateOrderData,
  OnShippingChangeActions,
  OnShippingChangeData,
} from "@paypal/paypal-js/types/components/buttons";
import {isToNorthIreland} from "../../../checkout/steps/AddressStep/AddressStep";
import {buttonStyleObjectExpress, buttonStyleObjectPDP, PayPalLoadingSpinner} from "./BraintreeButton";
import {prepareAddress} from "./helpers/paypalAddressHelpers";
import {ButtonPropType, IPartialCartData} from "./helpers/paypalTypes";
import {FUNDING_SOURCE} from "@paypal/paypal-js";
import {dbqs} from "../applepay/applePayHelperFunctions";
import {decideCartContent, restoreCart} from "../decideCartContent";
import braintree from "braintree-web";
import {Exponea} from "../../../../tracking/exponea";

/**
 * This component needs the PaypalScriptProvider to be loaded, so a braintree token has to exist
 * @param handleError
 * @param isCartBlocked
 * @param onReturn
 * @constructor
 */
const PayPalExpressButtons: React.FC<ButtonPropType> = ({handleError, isBlocked, onReturn, place}) => {
  const context = useUnsafeCartContext();
  const btppCheckout = useRef<braintree.PayPalCheckout>();
  const contextRef = useRef<IUnsafeCartContext>(null);
  contextRef.current = context;
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const checkoutRef = useRef<Checkout>();
  const [braintreeNamespace, setBraintreeNamespace] = useState<BraintreeNamespace>();

  useEffect(() => {
    if (!context?.braintreeData) {
      return;
    }

    // This is due to some derivation in type dev version
    /* eslint-disable @typescript-eslint/ban-ts-comment */
    setBraintreeNamespace({
      // @ts-ignore
      client: braintree.client,
      // @ts-ignore
      paypalCheckout: braintree.paypalCheckout,
    });
    /* eslint-enable @typescript-eslint/ban-ts-comment */
  }, [context]);

  const onError = onErrorClosure(handleError, "PayPal Express buttons");

  /**
   * BEFORE OPENING POPUP <br/>
   * I'm not using checkoutContext on purpose making sure to only generate a checkout when button is
   * actually clicked.
   */
  const createOrder = async function (data, actions: CreateBillingAgreementActions) {
    try {
      let cart: Cart;
      if (place === "pdp") {
        const datasetItemId = dbqs<HTMLElement>("#AddToCartButtonRoot")?.itemid;

        if (!datasetItemId) throw Error("createOrder: no product data given!");
        if (!contextRef.current) throw Error("createOrder: context is null!");

        cart = await decideCartContent(place, contextRef.current, Number(datasetItemId));
      } else {
        if (!isCart(context)) {
          // Should never be the case
          throw Error("No cart context in shopping cart on PayPal createOrder()");
        }
        cart = context.cart;
      }

      checkoutRef.current = await retrieveCheckout(cart, Payments.PaypalExpressBraintree);

      const apiShippingOptions = await checkoutRef.current.getAvailableShippingOptions();

      const paymentOptions = getPaymentOptions({
        cartData: checkoutRef.current.cartData,
        currentLogisticsTypeId: checkoutRef.current.logisticsTypeId,
        deliveryAddress: undefined,
        apiShippingOptions,
      });

      btppCheckout.current = actions.braintree as unknown as braintree.PayPalCheckout;

      return await actions.braintree.createPayment(paymentOptions);
    } catch (createOrderError) {
      (createOrderError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnCreateOrder;
      (createOrderError as ErrorPlaceInfoType).paymentSource = (data as CreateOrderData).paymentSource;

      // Regular onError call
      onError(createOrderError as Error | Record<string, unknown>);
      void checkoutRef.current?.put({paymentId: null});
      // Manual throw. This is because otherwise this return path
      // couldn't return anything without causing another error.
      // Will be directed to the onError, and there, will be ignored
      throw createOrderError;
    }
  };

  /**
   * AFTER POPUP CLOSING
   * Errors will go to paypal onError method
   * @param data
   * @param actions
   */
  const onApprove = async (
    data: BraintreePayPalCheckoutTokenizationOptions,
    actions: CreateBillingAgreementActions,
  ) => {
    try {
      const tokenized = await actions.braintree.tokenizePayment(data);

      if (!checkoutRef.current) {
        const noCheckoutRefError = new Error("There's no checkoutRef.current!");
        (noCheckoutRefError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
        onError(noCheckoutRefError);

        return;
      }

      if (!tokenized.details.shippingAddress) {
        const addressError = new Error("checkout.noValidPayPalAddress");
        (addressError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
        onError(addressError);
        void checkoutRef.current?.put({paymentId: null});

        return;
      }

      if (!isCart(contextRef.current)) {
        return;
      }

      const streetAndNum = tokenized.details.shippingAddress.line1;
      if (streetAndNum.includes("Packstation") || streetAndNum.includes("Postfiliale")) {
        const packingStationError = new Error("delivery to packing station not available for logistics type.");
        (packingStationError as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
        onError(packingStationError);
        void checkoutRef.current?.put({paymentId: null});

        return;
      }
      const {billingAddress, shippingAddress, email} = tokenized.details;
      const deliveryAddress = prepareAddress(shippingAddress, checkoutRef.current?.invoiceAddress?.email || email);
      // const invoiceAddress = prepareAddress(billingAddress, email);

      const logisticsTypeId = checkoutRef.current.logisticsTypeId;
      await checkoutRef.current.put({
        address: {
          delivery: {
            ...deliveryAddress,
            email: deliveryAddress.email,
            phone: checkoutRef.current?.deliveryAddress?.phone || deliveryAddress.phone,
          },
          invoice: {
            ...deliveryAddress,
            email: deliveryAddress.email,
          },
        },
        logisticsTypeId,
      } as ICheckoutPutParams);

      setPayPalInfoOnViewState(
        tokenized.nonce,
        contextRef.current,
        (data as BraintreePayPalCheckoutTokenizationOptions & {paymentSource: FUNDING_SOURCE}).paymentSource,
        place,
      );
      if (place === "pdp") Exponea.trackPdpPurchase("paypal_pdp");

      onReturn();
    } catch (err) {
      (err as ErrorPlaceInfoType).place = INTERNAL_ERROR_TYPE.PP_EXPRESS.OnApprove;
      (err as ErrorPlaceInfoType).paymentSource = (
        data as BraintreePayPalCheckoutTokenizationOptions & {paymentSource: FUNDING_SOURCE}
      ).paymentSource;
      onError(err as Error | Record<string, unknown>);
      void checkoutRef.current?.put({paymentId: null});
    }
  };

  const onShippingChange = async (data: OnShippingChangeData, actions: OnShippingChangeActions) => {
    // region Check for correct delivery address
    if (!isCart(contextRef.current)) {
      // Runtime error, shouldn't be the case
      throw Error("Sorry, something went wrong");
    }

    const countryCode = data.shipping_address?.country_code;
    const postalCode = data.shipping_address?.postal_code;
    const selectedShippingId = Number(data.selected_shipping_option?.id || "0");

    const {cart, countries} = contextRef.current;

    let isError: string | false = false;
    if (!countryCode || !postalCode) {
      // Eventually no address at all
      isError = "Shipping address has no country code or postal code";
    } else if (!countries.some((country) => country.countryCode === countryCode) || selectedShippingId === 0) {
      // Country code not allowed
      // or, no (valid) shipping id selected
      isError = "Unfortunately we cannot ship to this country";
    } else if (isToNorthIreland(countryCode, postalCode)) {
      // North Ireland also not allowed
      isError = "Due to toll concerns we cannot ship to north ireland";
    }
    if (isError) {
      // No idea if this has any effect, but there are some examples, that give
      // the clue, we can handle the error appearance in the modal
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      await actions.reject(isError);

      return;
    }
    // endregion

    const paymentId = data.paymentID || data.orderID;
    if (!checkoutRef.current || !paymentId) {
      await actions.reject();

      return;
    }

    const changeCart = cart.countryCode !== countryCode;
    // Only set if the new one is actually different to the already set one
    const changeCheckout = checkoutRef.current.logisticsTypeId !== selectedShippingId;

    let apiShippingOptions: IGetCheckoutShippingOptionsData[] | undefined;
    let cartData: IPartialCartData | undefined;
    if (changeCart) {
      await cart.put({countryCode});
      cart.positions.some((pos) =>
        pos.messages.some((msg) => {
          if (["NOT_ALLOWED_FOR_COUNTRY", "SHIPPING_NOT_FOUND"].includes(msg.code)) {
            isError = msg.code;

            return true;
          } else {
            return false;
          }
        }),
      );
      if (isError) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await actions.reject(isError);

        return;
      }

      apiShippingOptions = await checkoutRef.current.getAvailableShippingOptions();

      if (!changeCheckout) {
        cartData = {
          positions: cart.positions,
          rebates: cart.rebates,
          currencyCode: cart.currencyCode,
          value: {
            payable: cart.valuePayable,
          },
          isNet: cart.isNet,
        };
      }
    }

    try {
      if (changeCheckout) {
        const shippingOption = findShippingOption(apiShippingOptions || [], selectedShippingId);
        if (!apiShippingOptions || shippingOption) {
          // Cart didn't change, and chosen shipping is one of the formers
          // Or, cart did change, but a matching shipping was not found
          await checkoutRef.current.put({logisticsTypeId: shippingOption?.logisticsTypeId || selectedShippingId});
          cartData = checkoutRef.current.cartData;
        } else {
          // No shipping for country found
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          await actions.reject("SHIPPING_NOT_FOUND");

          return;
        }
      }
      if (cartData) {
        await btppCheckout.current?.updatePayment(
          getNewPaymentOptions(cartData, checkoutRef.current.logisticsTypeId, paymentId, apiShippingOptions),
        );
      }
      await actions.resolve();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      await actions.reject((e as Error).message);
    }
    // endregion
  };

  /**
   * Reset paymentId, when customer cancels PP payment
   */
  const onCancel = () => {
    void checkoutRef.current?.put({paymentId: null});
    if (isCart(contextRef.current)) void restoreCart(contextRef.current.cart);
  };

  if (!braintreeNamespace) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <div
        className={`${place === "pdp" ? "apple-pay-button-container" : "paypal-loading-div"}${
          isBlocked ? " blocked" : ""
        }`}
      >
        {!isInitialized && <PayPalLoadingSpinner classes="bottom-margin absolute" />}
        <BraintreePayPalButtons
          braintreeNamespace={braintreeNamespace}
          className="paypal-buttons-container"
          disabled={isBlocked || !isInitialized}
          style={place === "pdp" ? buttonStyleObjectPDP : buttonStyleObjectExpress}
          onError={onError}
          createOrder={createOrder}
          onApprove={onApprove}
          onShippingChange={onShippingChange}
          onCancel={onCancel}
          onInit={() => setIsInitialized(true)}
        />
      </div>
    </Suspense>
  );
};

export default PayPalExpressButtons;
